import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Button,
    Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    Avatar, CircularProgress,
} from '@mui/material';
import {
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    QuestionMark as MaybeIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Share as ShareIcon,
    HowToVote as CloseVoteIcon,
    CheckCircle as WinnerIcon,
    Star as BestMatchIcon,
    CalendarMonth as CalendarIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
    Person as PersonIcon,
    ArrowBack as BackIcon,
    EventNote as EventIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { events, votes } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { shareOrCopy } from '../utils/share';
import InvitationForm from '../components/InvitationForm';
import InvitationList from '../components/InvitationList';

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.1) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const FadeUp = ({ children, delay = 0 }) => {
    const [ref, inView] = useInView();
    return (
        <Box ref={ref} sx={{
            opacity:   inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(32px)',
            transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
            willChange: 'opacity, transform',
        }}>
            {children}
        </Box>
    );
};

// ── EventDetails ──────────────────────────────────────────────────────────────
const EventDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const scrollY = useScrollY();

    const [event,            setEvent]            = useState(null);
    const [error,            setError]            = useState('');
    const [loading,          setLoading]          = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [shareCopied,      setShareCopied]      = useState(false);
    const [votingSlotId,     setVotingSlotId]     = useState(null); // optimistic feedback
    const isMounted = useRef(true);

    const fetchEventDetails = async () => {
        try {
            const response = await events.getById(id);
            if (response.data) setEvent(response.data);
            else setError('No event data received');
        } catch (err) {
            setError(
                err.response?.status === 403
                    ? 'You do not have permission to view this event.'
                    : 'Failed to load event details. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEventDetails();
        return () => { isMounted.current = false; };
    }, [id]);

    const handleVote = async (timeSlotId, status) => {
        setVotingSlotId(timeSlotId);
        try {
            await votes.cast(timeSlotId, user.id, status);
            fetchEventDetails();
        } catch { setError('Failed to submit vote'); }
        finally { setVotingSlotId(null); }
    };

    const handleDeleteConfirm = async () => {
        try {
            await events.delete(id);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete event.');
        }
        setDeleteDialogOpen(false);
    };

    const handleShare = async () => {
        const url = `${window.location.origin}/vote/${event.shareToken}`;
        const result = await shareOrCopy({ title: event?.title, text: `Vote on a time for: ${event?.title}`, url });
        if (result === 'copied') {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2500);
        }
    };

    const handleCloseVoting = async () => {
        try { await events.closeVoting(id); fetchEventDetails(); }
        catch { setError('Failed to close voting'); }
    };

    const handleSelectTimeSlot = async (timeSlotId) => {
        try { await events.selectTimeSlot(id, timeSlotId); fetchEventDetails(); }
        catch { setError('Failed to select time slot'); }
    };

    const getBestSlotId = () => {
        if (!event?.timeSlots?.length) return null;
        let best = null, bestScore = -1;
        event.timeSlots.forEach(slot => {
            const score = (slot.votes?.filter(v => v.status === 'YES').length || 0) * 2
                        + (slot.votes?.filter(v => v.status === 'MAYBE').length || 0);
            if (score > bestScore) { bestScore = score; best = slot.id; }
        });
        return bestScore > 0 ? best : null;
    };

    const isVotingOpen = event
        ? !event.timeSlots?.some(s => s.selected) &&
          event.status !== 'CLOSED' &&
          !(event.votingDeadline && new Date(event.votingDeadline) < new Date())
        : false;

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    // ── Error / not found ─────────────────────────────────────────────────────
    if (error && !event) return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Box sx={{ textAlign:'center', maxWidth:420, px:3 }}>
                <Box sx={{ width:80, height:80, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3 }}>
                    <EventIcon sx={{ fontSize:36, color:'#7C3AED' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} gutterBottom>Something went wrong</Typography>
                <Typography color="text.secondary" sx={{ mb:3 }}>{error}</Typography>
                <Button onClick={() => navigate('/dashboard')} startIcon={<BackIcon />}
                    sx={{ fontWeight:700, color:'#7C3AED' }}>
                    Back to Dashboard
                </Button>
            </Box>
        </Box>
    );

    if (!event) return null;

    const isOrganizer = event.organizer?.id === user?.id;
    const bestSlotId  = getBestSlotId();
    const selectedSlot = event.timeSlots?.find(s => s.selected);

    const statusColor = {
        OPEN:   { bg:'#ECFDF5', text:'#059669', border:'#6EE7B7' },
        CLOSED: { bg:'#FEF3C7', text:'#D97706', border:'#FCD34D' },
        DRAFT:  { bg:'#F1F5F9', text:'#64748B', border:'#CBD5E1' },
    }[event.status] || { bg:'#F1F5F9', text:'#64748B', border:'#CBD5E1' };

    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC' }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box sx={{ background:'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', py:6, position:'relative', overflow:'clip' }}>
                {/* Parallax orbs */}
                <Box sx={{ position:'absolute', top:-60, left:'-4%', width:360, height:360, borderRadius:'50%', background:'rgba(255,255,255,0.07)', transform:`translateY(${scrollY*0.28}px)`, willChange:'transform', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', top:-20, right:'2%', width:280, height:280, borderRadius:'50%', background:'rgba(255,255,255,0.05)', transform:`translateY(${scrollY*0.45}px)`, willChange:'transform', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', bottom:-40, left:'48%', width:180, height:180, borderRadius:'50%', background:'rgba(255,255,255,0.06)', transform:`translateY(${scrollY*0.6}px)`, willChange:'transform', pointerEvents:'none' }} />

                <Container maxWidth="xl" sx={{ position:'relative', zIndex:1, transform:`translateY(${scrollY*0.18}px)`, willChange:'transform' }}>
                    {/* Back link */}
                    <Button onClick={() => navigate('/dashboard')} startIcon={<BackIcon />}
                        sx={{ color:'rgba(255,255,255,0.7)', fontWeight:600, mb:2, px:0, '&:hover':{ color:'white', bgcolor:'transparent' } }}>
                        Dashboard
                    </Button>

                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row' }, alignItems:{ sm:'flex-start' }, justifyContent:'space-between', gap:3 }}>
                        {/* Title block */}
                        <Box sx={{ display:'flex', gap:2.5, alignItems:'flex-start' }}>
                            <Avatar sx={{ width:64, height:64, fontSize:28, fontWeight:800, bgcolor:'rgba(255,255,255,0.18)', border:'2px solid rgba(255,255,255,0.3)', flexShrink:0 }}>
                                {event.title?.[0]?.toUpperCase() || 'E'}
                            </Avatar>
                            <Box>
                                <Box sx={{ display:'flex', alignItems:'center', gap:1.5, flexWrap:'wrap', mb:0.5 }}>
                                    <Typography variant="h4" fontWeight={800} sx={{ color:'white', lineHeight:1.1 }}>
                                        {event.title}
                                    </Typography>
                                    <Box sx={{ px:1.5, py:0.3, borderRadius:99, bgcolor: statusColor.bg, border:`1px solid ${statusColor.border}` }}>
                                        <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color: statusColor.text, textTransform:'uppercase', letterSpacing:0.8 }}>
                                            {event.status}
                                        </Typography>
                                    </Box>
                                </Box>
                                {event.description && (
                                    <Typography sx={{ color:'rgba(255,255,255,0.75)', fontSize:'1rem', maxWidth:600, lineHeight:1.6 }}>
                                        {event.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', flexShrink:0 }}>
                            <Button variant="contained" startIcon={shareCopied ? <CopyIcon /> : <ShareIcon />}
                                onClick={handleShare}
                                sx={{ fontWeight:700, borderRadius:2.5, bgcolor:'rgba(255,255,255,0.18)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.25)', color:'white', '&:hover':{ bgcolor:'rgba(255,255,255,0.28)' }, boxShadow:'none' }}>
                                {shareCopied ? 'Copied!' : 'Share Link'}
                            </Button>
                            {isOrganizer && isVotingOpen && (
                                <Button variant="contained" startIcon={<CloseVoteIcon />} onClick={handleCloseVoting}
                                    sx={{ fontWeight:700, borderRadius:2.5, bgcolor:'rgba(255,255,255,0.18)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.25)', color:'white', '&:hover':{ bgcolor:'rgba(255,255,255,0.28)' }, boxShadow:'none' }}>
                                    Close Voting
                                </Button>
                            )}
                            {isOrganizer && (
                                <>
                                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/events/${id}/edit`)}
                                        sx={{ fontWeight:700, borderRadius:2.5, borderColor:'rgba(255,255,255,0.4)', color:'white', '&:hover':{ borderColor:'white', bgcolor:'rgba(255,255,255,0.1)' } }}>
                                        Edit
                                    </Button>
                                    <Button variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}
                                        sx={{ fontWeight:700, borderRadius:2.5, borderColor:'rgba(255,100,100,0.5)', color:'rgba(255,180,180,1)', '&:hover':{ borderColor:'rgba(255,100,100,0.9)', bgcolor:'rgba(255,60,60,0.15)' } }}>
                                        Delete
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <Container maxWidth="xl" sx={{ py:5 }}>
                {error && <Alert severity="error" sx={{ mb:3, borderRadius:3 }}>{error}</Alert>}

                <Box sx={{ display:'flex', gap:3, alignItems:'flex-start', flexDirection:{ xs:'column', md:'row' } }}>

                    {/* LEFT: Time slots */}
                    <Box sx={{ flex:'1 1 0', minWidth:0 }}>
                        <FadeUp delay={0.06}>
                            <Typography variant="h6" fontWeight={800} sx={{ mb:2, color:'#0F172A', letterSpacing:-0.3 }}>
                                Time Slots
                                {event.timeSlots?.length > 0 && (
                                    <Typography component="span" sx={{ ml:1.5, fontSize:'0.8rem', fontWeight:600, color:'text.secondary' }}>
                                        {event.timeSlots.length} option{event.timeSlots.length !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Typography>
                        </FadeUp>

                        {event.timeSlots && event.timeSlots.length > 0 ? (
                            <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                                {event.timeSlots.map((slot, idx) => {
                                    const myVote  = slot.votes?.find(v => v.userId === user?.id)?.status || null;
                                    const isBest  = !slot.selected && slot.id === bestSlotId;
                                    const yesCount   = slot.votes?.filter(v => v.status === 'YES').length   || 0;
                                    const maybeCount = slot.votes?.filter(v => v.status === 'MAYBE').length || 0;
                                    const noCount    = slot.votes?.filter(v => v.status === 'NO').length    || 0;
                                    const totalVotes = yesCount + maybeCount + noCount;

                                    return (
                                        <FadeUp key={slot.id} delay={0.06 + idx * 0.05}>
                                            <Paper elevation={0} variant="outlined" sx={{
                                                borderRadius: 4,
                                                overflow: 'hidden',
                                                borderColor: slot.selected ? '#22C55E'
                                                           : isBest       ? '#7C3AED'
                                                           : 'rgba(0,0,0,0.1)',
                                                borderWidth: (slot.selected || isBest) ? 2 : 1,
                                                transition: 'box-shadow 0.2s',
                                                '&:hover': { boxShadow:'0 4px 20px rgba(0,0,0,0.07)' },
                                            }}>
                                                {/* Slot header */}
                                                <Box sx={{
                                                    px:3, py:2.5,
                                                    background: slot.selected
                                                        ? 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)'
                                                        : isBest
                                                        ? 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)'
                                                        : '#FAFAFA',
                                                    display:'flex', alignItems:'center', justifyContent:'space-between', gap:2, flexWrap:'wrap',
                                                    borderBottom:'1px solid', borderColor: slot.selected ? 'transparent' : isBest ? 'transparent' : 'divider',
                                                }}>
                                                    <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                                        <Box sx={{ width:40, height:40, borderRadius:2, bgcolor: slot.selected||isBest ? 'rgba(255,255,255,0.18)' : '#EDE9FE', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                            <CalendarIcon sx={{ fontSize:20, color: slot.selected||isBest ? 'white' : '#7C3AED' }} />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: slot.selected||isBest ? 'white' : '#0F172A', lineHeight:1.2 }}>
                                                                {slot.allDay
                                                                    ? format(new Date(slot.proposedStartTime), 'EEEE, MMMM d, yyyy')
                                                                    : format(new Date(slot.proposedStartTime), 'EEEE, MMMM d, yyyy')}
                                                            </Typography>
                                                            {!slot.allDay && (
                                                                <Typography variant="body2" sx={{ color: slot.selected||isBest ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontWeight:500 }}>
                                                                    {format(new Date(slot.proposedStartTime), 'h:mm a')} – {format(new Date(slot.proposedEndTime), 'h:mm a')}
                                                                </Typography>
                                                            )}
                                                            {slot.allDay && (
                                                                <Typography variant="body2" sx={{ color: slot.selected||isBest ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                                                                    All day
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
                                                        {slot.selected && (
                                                            <Chip icon={<WinnerIcon sx={{ fontSize:14 }} />} label="Winner" size="small"
                                                                sx={{ bgcolor:'rgba(255,255,255,0.25)', color:'white', fontWeight:700, border:'1px solid rgba(255,255,255,0.4)', '& .MuiChip-icon':{ color:'white' } }} />
                                                        )}
                                                        {isBest && (
                                                            <Chip icon={<BestMatchIcon sx={{ fontSize:14 }} />} label="Best Match" size="small"
                                                                sx={{ bgcolor:'rgba(255,255,255,0.22)', color:'white', fontWeight:700, border:'1px solid rgba(255,255,255,0.35)', '& .MuiChip-icon':{ color:'white' } }} />
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* Votes + actions */}
                                                <Box sx={{ px:3, py:2.5, bgcolor:'white' }}>
                                                    {totalVotes > 0 ? (
                                                        <Box sx={{ mb:2.5 }}>
                                                            {/* Tally pills */}
                                                            <Box sx={{ display:'flex', gap:1, mb:2, flexWrap:'wrap' }}>
                                                                {yesCount > 0 && (
                                                                    <Box sx={{ display:'flex', alignItems:'center', gap:0.5, px:1.5, py:0.4, borderRadius:99, bgcolor:'#F0FDF4', border:'1px solid #BBF7D0' }}>
                                                                        <ThumbUpIcon sx={{ fontSize:12, color:'#22C55E' }} />
                                                                        <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#16A34A' }}>{yesCount}</Typography>
                                                                    </Box>
                                                                )}
                                                                {maybeCount > 0 && (
                                                                    <Box sx={{ display:'flex', alignItems:'center', gap:0.5, px:1.5, py:0.4, borderRadius:99, bgcolor:'#FFFBEB', border:'1px solid #FDE68A' }}>
                                                                        <MaybeIcon sx={{ fontSize:12, color:'#F59E0B' }} />
                                                                        <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#B45309' }}>{maybeCount}</Typography>
                                                                    </Box>
                                                                )}
                                                                {noCount > 0 && (
                                                                    <Box sx={{ display:'flex', alignItems:'center', gap:0.5, px:1.5, py:0.4, borderRadius:99, bgcolor:'#FFF1F2', border:'1px solid #FECDD3' }}>
                                                                        <ThumbDownIcon sx={{ fontSize:12, color:'#F87171' }} />
                                                                        <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#DC2626' }}>{noCount}</Typography>
                                                                    </Box>
                                                                )}
                                                                <Typography variant="caption" color="text.disabled" sx={{ alignSelf:'center', ml:0.5 }}>
                                                                    {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
                                                                </Typography>
                                                            </Box>
                                                            {/* Grouped voter names — compact, no chips */}
                                                            {[
                                                                { key:'YES',   label:'Yes',   color:'#15803D', voters: slot.votes?.filter(v=>v.status==='YES')   || [] },
                                                                { key:'MAYBE', label:'Maybe', color:'#92400E', voters: slot.votes?.filter(v=>v.status==='MAYBE') || [] },
                                                                { key:'NO',    label:'No',    color:'#991B1B', voters: slot.votes?.filter(v=>v.status==='NO')    || [] },
                                                            ].filter(g => g.voters.length > 0).map(group => (
                                                                <Box key={group.key} sx={{ display:'flex', gap:0.75, alignItems:'baseline', mb:0.5 }}>
                                                                    <Typography variant="caption" fontWeight={700} sx={{ color: group.color, minWidth:34, fontSize:'0.68rem', textTransform:'uppercase', letterSpacing:0.4 }}>
                                                                        {group.label}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight:1.4 }}>
                                                                        {group.voters.map(v => v.username || v.guestName || 'Guest').join(', ')}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb:2, fontStyle:'italic', fontSize:'0.85rem' }}>
                                                            No votes yet
                                                        </Typography>
                                                    )}

                                                    {/* Voting buttons */}
                                                    <Box sx={{ display:'flex', gap:1.5, alignItems:'center', flexWrap:'wrap' }}>
                                                        {isVotingOpen && (
                                                            <>
                                                                {[
                                                                    { status:'YES',   label:'Yes',   icon:<ThumbUpIcon sx={{ fontSize:16 }} />,   activeBg:'#16A34A', activeBorder:'#16A34A', inactiveBorder:'#86EFAC', inactiveBg:'#F0FDF4', inactiveText:'#16A34A' },
                                                                    { status:'MAYBE', label:'Maybe', icon:<MaybeIcon sx={{ fontSize:16 }} />,   activeBg:'#D97706', activeBorder:'#D97706', inactiveBorder:'#FDE68A', inactiveBg:'#FFFBEB', inactiveText:'#D97706' },
                                                                    { status:'NO',    label:'No',    icon:<ThumbDownIcon sx={{ fontSize:16 }} />, activeBg:'#DC2626', activeBorder:'#DC2626', inactiveBorder:'#FCA5A5', inactiveBg:'#FFF1F2', inactiveText:'#DC2626' },
                                                                ].map(({ status, label, icon, activeBg, activeBorder, inactiveBorder, inactiveBg, inactiveText }) => {
                                                                    const active = myVote === status;
                                                                    return (
                                                                        <Button key={status} size="small" startIcon={icon}
                                                                            onClick={() => handleVote(slot.id, status)}
                                                                            disabled={votingSlotId === slot.id}
                                                                            sx={{
                                                                                fontWeight:700, borderRadius:99, px:2, py:0.8,
                                                                                border:'1.5px solid',
                                                                                borderColor: active ? activeBorder : inactiveBorder,
                                                                                bgcolor:     active ? activeBg    : inactiveBg,
                                                                                color:       active ? 'white'     : inactiveText,
                                                                                '&:hover': { bgcolor: active ? activeBg : inactiveBg, filter:'brightness(0.95)' },
                                                                                transition:'all 0.15s',
                                                                            }}>
                                                                            {label}
                                                                        </Button>
                                                                    );
                                                                })}
                                                            </>
                                                        )}
                                                        {isOrganizer && !isVotingOpen && !slot.selected && (
                                                            <Button size="small" variant="contained"
                                                                onClick={() => handleSelectTimeSlot(slot.id)}
                                                                sx={{ fontWeight:700, borderRadius:99, px:2.5,
                                                                    background: isBest
                                                                        ? 'linear-gradient(135deg,#7C3AED,#4F46E5)'
                                                                        : 'linear-gradient(135deg,#374151,#1F2937)',
                                                                    boxShadow:'none',
                                                                }}>
                                                                Select This Time
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </FadeUp>
                                    );
                                })}
                            </Box>
                        ) : (
                            <FadeUp delay={0.1}>
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, p:5, textAlign:'center', borderColor:'rgba(0,0,0,0.1)', bgcolor:'white' }}>
                                    <Box sx={{ width:64, height:64, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2 }}>
                                        <CalendarIcon sx={{ fontSize:30, color:'#7C3AED' }} />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>No time slots yet</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Time options will appear here once they're added.
                                    </Typography>
                                </Paper>
                            </FadeUp>
                        )}

                        {/* Invitations (organizer only) */}
                        {isOrganizer && (
                            <FadeUp delay={0.18}>
                                <Box sx={{ mt:4 }}>
                                    <InvitationForm eventId={event.id} onInvitationsSent={fetchEventDetails} />
                                    <InvitationList eventId={event.id} />
                                </Box>
                            </FadeUp>
                        )}
                    </Box>

                    {/* RIGHT: Event details sidebar */}
                    <Box sx={{ width:{ xs:'100%', md:320 }, flexShrink:0 }}>
                        <FadeUp delay={0.12}>
                            <Box sx={{ position:{ md:'sticky' }, top:24, display:'flex', flexDirection:'column', gap:2.5 }}>

                                {/* Event info card */}
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>
                                    <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)' }}>
                                        <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700 }}>
                                            Event Details
                                        </Typography>
                                    </Box>
                                    <Box sx={{ px:3, py:3, bgcolor:'white', display:'flex', flexDirection:'column', gap:2 }}>
                                        {event.location && (
                                            <Box sx={{ display:'flex', gap:1.5, alignItems:'flex-start' }}>
                                                <LocationIcon sx={{ fontSize:18, color:'#7C3AED', mt:0.15 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block' }}>Location</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{event.location}</Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        {event.organizer && (
                                            <Box sx={{ display:'flex', gap:1.5, alignItems:'flex-start' }}>
                                                <PersonIcon sx={{ fontSize:18, color:'#7C3AED', mt:0.15 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block' }}>Organizer</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{event.organizer.username}</Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        {event.votingDeadline && (
                                            <Box sx={{ display:'flex', gap:1.5, alignItems:'flex-start' }}>
                                                <TimeIcon sx={{ fontSize:18, color:'#7C3AED', mt:0.15 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block' }}>Voting Deadline</Typography>
                                                    <Typography variant="body2" fontWeight={600}>{format(new Date(event.votingDeadline), 'MMM d, yyyy h:mm a')}</Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        {selectedSlot && (
                                            <Box sx={{ display:'flex', gap:1.5, alignItems:'flex-start' }}>
                                                <WinnerIcon sx={{ fontSize:18, color:'#16A34A', mt:0.15 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block' }}>Confirmed Time</Typography>
                                                    <Typography variant="body2" fontWeight={600} sx={{ color:'#16A34A' }}>
                                                        {selectedSlot.allDay
                                                            ? format(new Date(selectedSlot.proposedStartTime), 'MMM d, yyyy')
                                                            : `${format(new Date(selectedSlot.proposedStartTime), 'MMM d, yyyy h:mm a')}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>

                                {/* Add to Calendar (when time is confirmed) */}
                                {selectedSlot && (
                                    <Button
                                        component="a"
                                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:8081/api'}/events/${event.id}/export.ics`}
                                        variant="outlined" fullWidth startIcon={<CalendarIcon />}
                                        sx={{ fontWeight:700, borderRadius:2.5, py:1.3, borderColor:'#7C3AED', color:'#7C3AED', '&:hover':{ bgcolor:'#EDE9FE', borderColor:'#6D28D9' } }}>
                                        Add to Calendar
                                    </Button>
                                )}

                                {/* Voting status card */}
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, p:3, bgcolor:'white', borderColor:'rgba(0,0,0,0.1)' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:1.5 }}>
                                        Voting Status
                                    </Typography>
                                    <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                        <Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor: isVotingOpen ? '#22C55E' : '#9CA3AF', flexShrink:0 }} />
                                        <Typography variant="body2" fontWeight={600} color={ isVotingOpen ? '#16A34A' : 'text.secondary' }>
                                            {isVotingOpen ? 'Open — votes are being collected' : 'Closed'}
                                        </Typography>
                                    </Box>
                                    {event.timeSlots?.length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt:1, display:'block' }}>
                                            {event.timeSlots.reduce((n, s) => n + (s.votes?.length || 0), 0)} total vote{event.timeSlots.reduce((n, s) => n + (s.votes?.length || 0), 0) !== 1 ? 's' : ''} cast across {event.timeSlots.length} option{event.timeSlots.length !== 1 ? 's' : ''}
                                        </Typography>
                                    )}
                                </Paper>

                            </Box>
                        </FadeUp>
                    </Box>

                </Box>
            </Container>

            {/* ── Delete dialog ─────────────────────────────────────────────── */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx:{ borderRadius:4, p:1 } }}>
                <DialogTitle sx={{ fontWeight:800, pb:1 }}>Delete Event?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        This will permanently delete <strong>{event.title}</strong> and all its votes. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight:700, borderRadius:2.5 }}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error"
                        sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#EF4444,#DC2626)', boxShadow:'none' }}>
                        Delete Event
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventDetails;
